import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Download, History } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { formatErr } from '../billing/shared';

const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const DownloadReport = ({ id, label = 'Download Excel report', small }) => {
  const { authHeader } = useAuth();
  const go = async () => {
    try {
      const { data, headers } = await axios.get(`${API}/import-reports/${id}/xlsx`, { headers: authHeader(), responseType: 'blob' });
      const name = /filename="([^"]+)"/.exec(headers['content-disposition'] || '')?.[1] || 'import-report.xlsx';
      const a = document.createElement('a'); a.href = URL.createObjectURL(data); a.download = name; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <button type="button" onClick={go} className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white font-semibold text-slate-700 hover:border-slate-500 ${small ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'}`} data-testid={`report-download-${id}`}>
      <Download size={small ? 12 : 13} /> {label}
    </button>
  );
};

// Shown right after an import: what came in, which rows need fixing.
export const ImportReportView = ({ id }) => {
  const { authHeader } = useAuth();
  const [rep, setRep] = useState(null);
  useEffect(() => { axios.get(`${API}/import-reports/${id}`, { headers: authHeader() }).then((r) => setRep(r.data)).catch(() => {}); }, [id, authHeader]);
  if (!rep) return null;
  const fix = rep.issues || [];
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="import-report">
      <div className="flex flex-wrap items-center gap-2">
        <CheckCircle2 size={16} className="text-emerald-600" />
        <p className="text-sm font-semibold text-slate-900">Import finished</p>
        <span className="text-xs text-slate-500">{rep.total_rows} rows in {rep.filename || 'file'}</span>
        <span className="ml-auto"><DownloadReport id={rep.id} /></span>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="import-report-counts">
        {rep.counts.map((c) => <span key={c.label} className="rounded-lg bg-white px-2.5 py-1 text-xs ring-1 ring-slate-200">{c.label}: <b>{Number(c.value).toLocaleString('en-IN')}</b></span>)}
        <span className={`rounded-lg px-2.5 py-1 text-xs ring-1 ${rep.issue_count ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200'}`} data-testid="import-report-issue-count">
          Rows to fix: <b>{rep.issue_count}</b></span>
      </div>
      {!!fix.length && (
        <div className="max-h-56 overflow-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-xs" data-testid="import-report-issues">
            <thead className="sticky top-0 bg-slate-100 text-left"><tr><th className="px-2 py-1">Row</th><th className="px-2 py-1">What to fix</th></tr></thead>
            <tbody>{fix.map((i, n) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={n} className="border-t border-slate-100"><td className="whitespace-nowrap px-2 py-1 text-slate-500">{i.sheet ? `${i.sheet} · ` : ''}{i.row || '—'}</td>
                <td className="px-2 py-1"><span className="inline-flex gap-1"><AlertTriangle size={12} className={`mt-0.5 shrink-0 ${i.warn ? 'text-slate-400' : 'text-amber-600'}`} />{i.problem}</span></td></tr>))}</tbody>
          </table>
          {rep.issue_count > fix.length && <p className="p-2 text-center text-[11px] text-slate-500">Showing {fix.length} of {rep.issue_count}. Download the Excel for all.</p>}
        </div>)}
      {!!fix.length && <p className="text-[11px] text-slate-500">The Excel lists each of these rows with its original values. Fix them there and upload that sheet again; rows already imported are merged, not doubled.</p>}
    </div>
  );
};

export const ImportHistory = ({ refreshKey }) => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => axios.get(`${API}/import-reports`, { headers: authHeader() }).then((r) => setRows(r.data)).catch(() => setRows([])), [authHeader]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="import-history">
      <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900"><History size={15} /> Import history</h2>
      {rows && !rows.length && <p className="text-xs text-slate-500" data-testid="import-history-empty">No imports yet. Each import you run is listed here with its report.</p>}
      {!!rows?.length && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2 pr-3">When</th><th className="py-2 pr-3">What</th><th className="py-2 pr-3">File</th><th className="py-2 pr-3">Result</th><th className="py-2 pr-3">Rows to fix</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`import-history-row-${r.id}`}>
                <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-600">{when(r.at)}<span className="block text-[11px] text-slate-400">{r.by}</span></td>
                <td className="py-2 pr-3 font-medium">{r.title}</td>
                <td className="max-w-[180px] truncate py-2 pr-3 text-xs text-slate-500" title={r.filename}>{r.filename}</td>
                <td className="py-2 pr-3 text-xs">{r.counts.filter((c) => c.value).map((c) => `${c.label} ${Number(c.value).toLocaleString('en-IN')}`).join(' · ') || 'Nothing new'}</td>
                <td className={`py-2 pr-3 text-xs font-semibold ${r.issue_count ? 'text-amber-700' : 'text-emerald-700'}`}>{r.issue_count}</td>
                <td className="py-2 text-right"><DownloadReport id={r.id} label="Excel" small /></td>
              </tr>))}</tbody>
          </table>
        </div>)}
    </section>
  );
};
